"use client";

// Auth state for the web SaaS. Uses the same localStorage shape as
// /account so the existing plugin /account flow keeps working — we share a
// single session token, written by either page.

import { useCallback, useEffect, useState } from "react";
import {
  fetchEntitlements,
  type AuthenticatedUser,
  type Entitlements,
  ApiError,
} from "./api";

const TOKEN_KEY = "snap-site:web-auth-token";
const ACCOUNT_KEY = "snap-site:web-account";
const CLIENT_ID_KEY = "snap-site:web-client-id";
const CHANGE_EVENT = "snap-site:auth-changed";

export interface CachedAccount {
  email: string;
  plan: AuthenticatedUser["plan"];
  subscriptionStatus: string;
  credits: number;
  monthlyGrant: number;
}

export type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | {
      status: "authenticated";
      token: string;
      user: AuthenticatedUser;
      entitlements: Entitlements;
    };

export function readToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function readCachedAccount(): CachedAccount | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(ACCOUNT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CachedAccount;
  } catch {
    return null;
  }
}

export function writeAuth(token: string, account: CachedAccount): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function clearAuth(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(ACCOUNT_KEY);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function readClientId(): string {
  if (typeof window === "undefined") return "web-ssr";
  const existing = window.localStorage.getItem(CLIENT_ID_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `web-${crypto.randomUUID()}`
      : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(CLIENT_ID_KEY, id);
  return id;
}

export function accountFromPayload(payload: {
  user: AuthenticatedUser;
  entitlements: Entitlements;
}): CachedAccount {
  return {
    email: payload.user.email,
    plan: payload.user.plan,
    subscriptionStatus: payload.user.subscriptionStatus,
    credits: payload.entitlements.credits.balance,
    monthlyGrant: payload.entitlements.credits.monthlyGrant,
  };
}

export function planLabel(plan: AuthenticatedUser["plan"]): string {
  if (plan === "agency") return "Agency";
  if (plan === "pro") return "Pro";
  return "Free";
}

export function emailInitial(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return "?";
  return trimmed[0]?.toUpperCase() ?? "?";
}

// Hook used by every signed-in page. On mount it picks up the stored
// token, fetches fresh entitlements, and surfaces three states. Pages can
// react to "anonymous" by redirecting to /login.
export function useAuth(): AuthState & {
  refresh: () => Promise<void>;
  signOut: () => void;
} {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  const hydrate = useCallback(async () => {
    const token = readToken();
    if (!token) {
      setState({ status: "anonymous" });
      return;
    }
    try {
      const data = await fetchEntitlements(token);
      // Defensive: if the server accepted the request (2xx) but resolved us
      // to no user, our token isn't a valid session — treat it as expired
      // rather than rendering cached entitlements (which is how an expired
      // session ended up showing a stale Pro balance). The server now 401s
      // for an invalid Bearer token, but this guards any path that returns
      // an anonymous (user:null) shape to a token-bearing request.
      if (!data.user) {
        clearAuth();
        setState({ status: "anonymous" });
        return;
      }
      // Refresh the cached account silently. Do NOT call writeAuth here —
      // that fires CHANGE_EVENT, which is wired below to re-trigger
      // hydrate(), causing a runaway fetch loop. writeAuth is only for
      // explicit login transitions (different token).
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          ACCOUNT_KEY,
          JSON.stringify(accountFromPayload(data))
        );
      }
      setState({
        status: "authenticated",
        token,
        user: data.user,
        entitlements: data.entitlements,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clearAuth();
        setState({ status: "anonymous" });
        return;
      }
      // Network blip — keep the user on the cached identity so the page
      // still renders, even if entitlements aren't fresh.
      const cached = readCachedAccount();
      if (cached) {
        setState({
          status: "authenticated",
          token,
          user: {
            id: "cached",
            email: cached.email,
            plan: cached.plan,
            subscriptionStatus: cached.subscriptionStatus,
          },
          entitlements: {
            plan: cached.plan,
            subscriptionStatus: cached.subscriptionStatus,
            credits: { balance: cached.credits, monthlyGrant: cached.monthlyGrant },
            features: {
              mobile: cached.plan !== "free",
              bothViewports: cached.plan !== "free",
              aiInteractions: cached.plan !== "free",
              maxPagesPerRun: cached.plan === "agency" ? 30 : cached.plan === "pro" ? 15 : 5,
            },
            limits: { devices: 5, concurrentJobs: 3 },
          },
        });
      } else {
        setState({ status: "anonymous" });
      }
    }
  }, []);

  useEffect(() => {
    void hydrate();
    function onChange() {
      void hydrate();
    }
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [hydrate]);

  return {
    ...state,
    refresh: hydrate,
    signOut: () => {
      clearAuth();
      setState({ status: "anonymous" });
    },
  };
}
